import React, { useId, useState } from 'react';

import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import {
  UNIT_CISTERNS,
  UNIT_LITERS,
  cisternLabelOf,
  cisternLitersOf,
  cisternsFromLiters,
  litersFromCisterns,
  volumeEquivalentLabel,
} from '../../lib/volumeUnits';

/**
 * Saisie d'un volume, en litres ou en citernes, avec l'équivalent toujours
 * affiché dans l'autre unité.
 *
 * La conversion est la seule de l'application qui ne soit pas de présentation :
 * ce champ traduit avant l'appel API. La commande part en litres, plus le
 * nombre de citernes quand c'est ainsi qu'elle a été comptée.
 *
 * Deux pièges de saisie déjà rencontrés dans ce dépôt sont évités ici :
 *
 * 1. Le texte tapé n'est jamais réécrit à partir de la valeur convertie. Taper
 *    « 0. » puis relire « 0 » rendait toute décimale impossible à saisir. Le
 *    brouillon local fait foi tant que le champ a le focus ; il est relâché au
 *    blur, où l'affichage se recale sur la valeur canonique.
 * 2. Aucune `key` React ne dépend de la valeur éditée : remonter une clé
 *    remonterait le champ à chaque frappe et lui ferait perdre le focus.
 */
const CisternInput = ({
  liters,
  cisterns,
  settings,
  onChange,
  disabled = false,
  label = 'Volume',
  className,
}) => {
  const cisternLiters = cisternLitersOf(settings);
  const cisternLabel = cisternLabelOf(settings);

  // L'unité d'ouverture est celle dans laquelle la commande a été comptée.
  const [unit, setUnit] = useState(
    cisterns !== null && cisterns !== undefined && cisterns !== ''
      ? UNIT_CISTERNS
      : UNIT_LITERS,
  );
  const [draft, setDraft] = useState(null);
  const fieldId = useId();

  const litersValue = Number(liters || 0);

  const canonicalText = () => {
    if (unit === UNIT_CISTERNS) {
      if (cisterns !== null && cisterns !== undefined && cisterns !== '')
        return String(cisterns);
      return litersValue > 0
        ? String(
            Math.round(cisternsFromLiters(litersValue, cisternLiters) * 100) / 100,
          )
        : '';
    }
    return liters === null || liters === undefined || liters === ''
      ? ''
      : String(liters);
  };

  const shown = draft !== null ? draft : canonicalText();

  const emit = (rawText, nextUnit) => {
    // Une virgule décimale est ce que tape un clavier français.
    const parsed = Number(String(rawText).replace(',', '.'));
    const numeric = Number.isFinite(parsed) ? parsed : 0;

    if (nextUnit === UNIT_CISTERNS) {
      onChange({
        liters: litersFromCisterns(numeric, cisternLiters),
        cisterns: rawText === '' ? null : numeric,
      });
      return;
    }
    // Repasser en litres efface le comptage en citernes : garder les deux
    // laisserait deux nombres décrire le même volume.
    onChange({ liters: rawText === '' ? '' : numeric, cisterns: null });
  };

  const switchUnit = (nextUnit) => {
    if (nextUnit === unit) return;
    setDraft(null);
    setUnit(nextUnit);
    // Le volume ne bouge pas, seule sa lecture change.
    if (nextUnit === UNIT_CISTERNS) {
      onChange({
        liters: litersValue,
        cisterns: litersValue
          ? Math.round(cisternsFromLiters(litersValue, cisternLiters) * 100) / 100
          : null,
      });
    } else {
      onChange({ liters: litersValue, cisterns: null });
    }
  };

  const unitButton = (value, text) => (
    <button
      type="button"
      onClick={() => switchUnit(value)}
      disabled={disabled}
      aria-pressed={unit === value}
      className={cn(
        'h-7 rounded-md px-2.5 text-[11px] font-semibold transition-colors disabled:opacity-50',
        unit === value
          ? 'bg-card text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {text}
    </button>
  );

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={fieldId}>{label}</Label>
        <div className="flex items-center gap-0.5 rounded-lg bg-muted/60 p-0.5">
          {unitButton(UNIT_LITERS, 'L')}
          {unitButton(UNIT_CISTERNS, 'Citernes')}
        </div>
      </div>

      <Input
        id={fieldId}
        inputMode="decimal"
        disabled={disabled}
        value={shown}
        placeholder={unit === UNIT_CISTERNS ? 'ex. 30' : 'ex. 1 200 000'}
        onChange={(event) => {
          setDraft(event.target.value);
          emit(event.target.value, unit);
        }}
        onBlur={() => setDraft(null)}
      />

      <p className="text-xs text-muted-foreground">
        {unit === UNIT_CISTERNS
          ? `1 ${cisternLabel} = ${cisternLiters.toLocaleString('fr-FR')} L · soit ${volumeEquivalentLabel(litersValue, settings, UNIT_CISTERNS)}`
          : `soit ${volumeEquivalentLabel(litersValue, settings, UNIT_LITERS)}`}
      </p>
    </div>
  );
};

export default CisternInput;
export { CisternInput };
